import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { useAuth } from '../../hooks/useAuth';
import {
  adminActionCardSx,
  adminHeroSx,
  adminMetricIconSx,
  adminSoftChipSx,
  adminSummaryCardSx,
} from '../../components/admin/adminVisuals';
import { adminRoute } from '../../config/adminPath';

const sections = [
  {
    title: 'Usuários administrativos',
    description: 'Crie contas, acompanhe convites e organize quem pode acessar o painel.',
    to: adminRoute('users'),
    icon: <GroupRoundedIcon fontSize="large" />,
    accent: { background: 'rgba(37, 99, 235, 0.12)', color: '#1d4ed8' },
  },
  {
    title: 'Banners da página inicial',
    description: 'Organize destaques, imagens e destinos dos banners sem alterar a apresentação pública.',
    to: adminRoute('homepage/banners'),
    icon: <ImageRoundedIcon fontSize="large" />,
    accent: { background: 'rgba(245, 158, 11, 0.16)', color: '#b45309' },
  },
  {
    title: 'Popup promocional',
    description: 'Controle exibicao, imagem, texto do botao e link do popup exibido no site.',
    to: adminRoute('homepage/popup'),
    icon: <CampaignRoundedIcon fontSize="large" />,
    accent: { background: 'rgba(168, 85, 247, 0.14)', color: '#7e22ce' },
  },
  {
    title: 'Oportunidades e editais',
    description: 'Atualize editais e oportunidades com a mesma estrutura visual já usada no site.',
    to: adminRoute('homepage/editais'),
    icon: <DescriptionRoundedIcon fontSize="large" />,
    accent: { background: 'rgba(14, 165, 233, 0.14)', color: '#0369a1' },
  },
  {
    title: 'Documentos da Secad',
    description: 'Mantenha os documentos da Secretaria Acadêmica atualizados de forma clara e organizada.',
    to: adminRoute('secretaria/secad'),
    icon: <FolderRoundedIcon fontSize="large" />,
    accent: { background: 'rgba(20, 184, 166, 0.14)', color: '#0f766e' },
  },
  {
    title: 'Documentos da Monitoria',
    description: 'Substitua o manual e os editais que aparecem na página pública do Programa de Monitoria.',
    to: adminRoute('secretaria/monitoria'),
    icon: <FolderRoundedIcon fontSize="large" />,
    accent: { background: 'rgba(37, 99, 235, 0.12)', color: '#1d4ed8' },
  },
  {
    title: 'Documentos e relatórios da CPA',
    description: 'Publique portarias, resoluções e relatórios diretamente na página da CPA.',
    to: adminRoute('cpa/documentos'),
    icon: <FactCheckRoundedIcon fontSize="large" />,
    accent: { background: 'rgba(168, 85, 247, 0.14)', color: '#7e22ce' },
  },
];

export default function AdminDashboard() {
  const { user } = useAuth();

  return (
    <Stack spacing={3}>
      <Box sx={adminHeroSx}>
        <Stack spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
          <Chip label="Painel em operação" variant="outlined" sx={adminSoftChipSx} />
          <Typography variant="h4" fontWeight={900} sx={{ color: '#16324f' }}>
            Visão geral do painel
          </Typography>
          <Typography sx={{ maxWidth: 780, color: 'rgba(22, 50, 79, 0.88)' }}>
            Acompanhe os acessos e os módulos disponíveis em um ambiente mais leve, claro e preparado para a rotina
            administrativa.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(22, 50, 79, 0.72)' }}>
            Sessão atual: {user?.email ?? 'sem usuário'}
          </Typography>
        </Stack>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Card sx={adminSummaryCardSx}>
            <CardContent>
              <Stack spacing={1.75}>
                <Box sx={adminMetricIconSx('rgba(37, 99, 235, 0.12)', '#1d4ed8')}>
                  <ShieldRoundedIcon />
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                  Acesso
                </Typography>
                <Typography color="text.secondary">
                  Entrada por e-mail e senha, com sessão mantida para um uso contínuo do painel.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={adminSummaryCardSx}>
            <CardContent>
              <Stack spacing={1.75}>
                <Box sx={adminMetricIconSx('rgba(14, 165, 233, 0.14)', '#0369a1')}>
                  <HubRoundedIcon />
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                  Organização
                </Typography>
                <Typography color="text.secondary">
                  Conteúdos e acessos ficam reunidos em um fluxo único para facilitar a rotina administrativa.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={adminSummaryCardSx}>
            <CardContent>
              <Stack spacing={1.75}>
                <Box sx={adminMetricIconSx('rgba(20, 184, 166, 0.14)', '#0f766e')}>
                  <StorageRoundedIcon />
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                  Expansão
                </Typography>
                <Typography color="text.secondary">
                  A estrutura já está preparada para receber novas áreas e novos conteúdos no futuro.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {sections.map((section) => (
          <Grid item xs={12} md={6} key={section.to}>
            <Card sx={adminActionCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={adminMetricIconSx(section.accent.background, section.accent.color)}>{section.icon}</Box>
                  <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                    {section.title}
                  </Typography>
                  <Typography color="text.secondary">{section.description}</Typography>
                  <Button
                    component={RouterLink}
                    to={section.to}
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start', borderRadius: 999 }}
                  >
                    Abrir módulo
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
